import {
  Button,
  Container,
  Grid,
  Group,
  Modal,
  Select,
  Switch,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconArrowLeft, IconCheck, IconFileInfo, IconX } from '@tabler/icons';
import { useEffect, useState } from 'react';

import { CustomLoader } from '../../components/custom-loader';
import { CustomMultipleFileButton } from '../../components/custom-multiple-file-input';
import { rootGoBack, rootNavigate } from '../../components/custom-router';
import { Page } from '../../components/page';
import { getCompaniesMinRequest } from '../../data/company';
import { createOptionRequest, listOptionsRequest } from '../../data/options';
import {
  useAddEmployeeFiles,
  useCreateEmployee,
} from '../../data/hooks/employee';
import { useUploadMultipleFiles } from '../../data/hooks/files';
import { useCreateUser } from '../../data/hooks/user';
import { useGetWorkScaleList } from '../../data/hooks/work-scale';
import { getUsersRequest } from '../../data/user';
import { CompanyMin } from '../../models/company';
import { Employee, EmployeePeriod } from '../../models/employees';
import { User, UserRole, UserType } from '../../models/user';
import {
  errorNotification,
  successNotification,
} from '../../providers/mantine-notifications';
import { employeePeriodList } from '../../utils/constants/employee';
import { userRoleList } from '../../utils/constants/user';
import { AppRoutePaths } from '../../utils/enums/routes';
import { formatBRL, realToUSCash } from '../../utils/helpers';
import { formatCEP, formatCPF, formatRG } from '../../utils/masks';
import { SECTOR_OPTIONS } from '../../utils/constants/sectors';

export function UserCreate() {
  const [userInfoModal, setUserInfoModal] = useState(false);
  const [companyList, setCompanyList] = useState<CompanyMin[]>([]);
  const [sectorData, setSectorData] = useState(SECTOR_OPTIONS);
  const companyOptions = companyList?.map((item) => ({
    label: item.fantasyName,
    value: String(item.id),
  }));
  const [newUser, setNewUser] = useState<User>();
  const [userList, setUserList] = useState<User[]>([]);
  const [pageLoading, setPageLoading] = useState(false);

  const form = useForm({
    initialValues: {
      username: '',
      role: '',
      type: 'user',
      manualLogin: false,
      isSectorSupervisor: false,
      birthDate: '',
      files: [],
      employee: {
        paymentPrice: '',
        transportPrice: '',
        needTransport: false,
        documentRG: '',
        documentCPF: '',
        documentPIS: '',
        period: '',
        addressZipCode: '',
        addressStreet: '',
        addressNumber: '',
        addressCity: '',
        note: '',
        company: null,
        workScale: '',
        supervisor: '',
        salary: '',
        email: '',
        sector: '',
      },
    },
  });

  const { fetch: createUserFetcher, loading: createUserLoader } =
    useCreateUser();
  const { fetch: createEmployeeFetcher, loading: createEmployeeLoader } =
    useCreateEmployee();
  const {
    fetch: getWorkScaleListFetcher,
    response: getWorkScaleListResponse,
    loading: getWorkScaleListLoader,
  } = useGetWorkScaleList();

  const { fetch: addEmployeeFilesFetcher, loading: addEmployeeFilesLoading } =
    useAddEmployeeFiles();

  const { fetch: uploadFilesFetcher, loading: uploadFilesLoader } =
    useUploadMultipleFiles();

  async function handleSubmit({ employee, ...values }: typeof form.values) {
    const { files } = values;

    let fileIds: number[] = [];

    fileIds = await handleUploadFiles(files, 'Tente novamente.');

    await createUserFetcher({
      data: {
        ...values,
        role: values.role as UserRole,
        type: values.type as UserType,
      },
      onSuccess: async (user: User) => {
        await createEmployeeFetcher({
          userId: user.id,
          data: {
            ...employee,
            paymentPrice: employee.paymentPrice
              ? realToUSCash(employee.paymentPrice)
              : undefined,
            transportPrice: employee.transportPrice
              ? realToUSCash(employee.transportPrice)
              : undefined,
            period: employee.period as EmployeePeriod.DAY,
            company: employee.company ? Number(employee.company) : undefined,
            workScale: employee.workScale
              ? Number(employee.workScale)
              : undefined,
            supervisor: employee.supervisor
              ? Number(employee.supervisor)
              : undefined,
            salary: employee.salary ? realToUSCash(employee.salary) : undefined,
            sector: employee.sector || undefined,
          },
          onSuccess: async (employee: Employee) => {
            await addEmployeeFilesFetcher({
              employeeId: Number(employee.id),
              data: {
                files: fileIds.map((fileId) => fileId),
              },
            });
          },
        });
        setNewUser(user);
        successNotification({
          title: 'Usuário cadastrado.',
          message: 'tudo certo.',
        });
        setUserInfoModal(true);
        form.reset();
      },
    });
  }

  async function getCompanies() {
    const response = await getCompaniesMinRequest();
    setCompanyList(response);
  }

  async function getWorkScaleList() {
    await getWorkScaleListFetcher({});
  }

  async function getUsers() {
    try {
      setPageLoading(true);
      const [supervisors, managers] = await Promise.all([
        getUsersRequest({ isSectorSupervisor: true }),
        getUsersRequest({ type: UserType.MANAGER }),
      ]);
      const merged = [
        ...supervisors,
        ...managers.filter((m) => !supervisors.some((s) => s.id === m.id)),
      ];
      setUserList(merged);
      setPageLoading(false);
    } catch (error) {
      setPageLoading(false);
      errorNotification({
        title: 'Erro ao buscar usuários.',
        message: 'tente novamente',
      });
    }
  }

  async function handleUploadFiles(
    fileList: File[],
    errorMessage: string,
  ): Promise<number[]> {
    let fileIds: number[] = [];

    if (!fileList.length) return [];

    try {
      await uploadFilesFetcher({
        data: {
          files: fileList,
        },
        onSuccess: (res) => {
          fileIds = res;
        },
      });
      return fileIds;
    } catch (error) {
      errorNotification({
        title: 'Erro ao realizar upload dos arquivos',
        message: errorMessage,
      });
      return [];
    }
  }

  useEffect(() => {
    getCompanies();
    getWorkScaleList();
    getUsers();
    listOptionsRequest('sector').then((options) => {
      setSectorData((prev) => {
        const existing = new Set(prev.map((o) => o.value));
        const extras = options
          .filter((o) => !existing.has(o.name))
          .map((o) => ({ value: o.name, label: o.name }));
        return extras.length ? [...prev, ...extras] : prev;
      });
    });
  }, []);

  return (
    <Page>
      <CustomLoader loading={getWorkScaleListLoader || pageLoading} />
      <Container bg="white" h="100%">
        <Button
          leftIcon={<IconArrowLeft />}
          onClick={rootGoBack}
          mt={16}
          mb={16}
          variant="subtle"
        >
          voltar
        </Button>
        <Title>Cadastro de usuário</Title>
        <Text color="gray" mb={16}>
          dados do novo usuário
        </Text>
        <form onSubmit={form.onSubmit((values) => handleSubmit(values))}>
          <Grid columns={2} maw={700}>
            <Grid.Col span={1}>
              <TextInput
                required
                withAsterisk
                label="Nome"
                placeholder="nome completo"
                type="text"
                {...form.getInputProps('username')}
              />
            </Grid.Col>
            <Grid.Col span={1}>
              <Select
                required
                withAsterisk
                name="role"
                label="Tipo de usuário"
                placeholder="selecione o tipo e usuário"
                data={userRoleList}
                {...form.getInputProps('role')}
              />
            </Grid.Col>
            <Grid.Col span={1}>
              <Select
                required
                withAsterisk
                name="role"
                label="Periodo de trabalho"
                placeholder="selecione o periodo"
                data={employeePeriodList}
                {...form.getInputProps('employee.period')}
              />
            </Grid.Col>
            <Grid.Col span={1}>
              <Select
                searchable
                name="company"
                label="Empresa"
                placeholder="selecione a empresa"
                data={companyOptions ?? []}
                {...form.getInputProps('employee.company')}
              />
            </Grid.Col>
            <Grid.Col span={1}>
              <Select
                searchable
                name="workScale"
                label="Escala"
                placeholder="selecione a escala"
                data={
                  getWorkScaleListResponse?.map((item) => ({
                    label: item.name,
                    value: String(item.id),
                  })) ?? []
                }
                {...form.getInputProps('employee.workScale')}
              />
            </Grid.Col>
            <Grid.Col span={1}>
              <Select
                required
                withAsterisk
                searchable
                clearable
                label="Supervisor"
                placeholder="selecione o supervisor"
                data={userList?.map((item) => ({
                  label: item.username,
                  value: String(item.id),
                }))}
                mb={16}
                mr={8}
                {...form.getInputProps('employee.supervisor')}
              />
            </Grid.Col>
            <Grid.Col span={2}>
              <TextInput
                label="Email"
                placeholder="email"
                type="text"
                {...form.getInputProps('employee.email')}
              />
            </Grid.Col>
            <Grid.Col span={1}>
              <TextInput
                label="Data de Nascimento"
                placeholder="ex: 1990-12-31"
                type="date"
                {...form.getInputProps('birthDate')}
              />
            </Grid.Col>
            <Grid.Col span={2}>
              <Switch
                mt={8}
                mb={8}
                checked={form.values.manualLogin}
                color="teal"
                size="sm"
                label="Permitir acesso sem biometria?"
                thumbIcon={
                  form.values.manualLogin ? (
                    <IconCheck size="0.8rem" color="teal" stroke={3} />
                  ) : (
                    <IconX size="0.8rem" color="red" stroke={3} />
                  )
                }
                {...form.getInputProps('manualLogin')}
              />
            </Grid.Col>
            <Grid.Col span={2}>
              <Switch
                mt={8}
                mb={8}
                checked={form.values.isSectorSupervisor}
                color="blue"
                size="sm"
                label="Supervisor de setor?"
                thumbIcon={
                  form.values.isSectorSupervisor ? (
                    <IconCheck size="0.8rem" color="blue" stroke={3} />
                  ) : (
                    <IconX size="0.8rem" color="red" stroke={3} />
                  )
                }
                {...form.getInputProps('isSectorSupervisor')}
              />
            </Grid.Col>
            <Grid.Col span={2}>
              <Select
                label="Setor"
                placeholder="Selecione o setor"
                data={sectorData}
                searchable
                clearable
                creatable
                getCreateLabel={(q) => `+ Adicionar "${q}"`}
                onCreate={(q) => {
                  const item = { value: q, label: q };
                  setSectorData((prev) => [...prev, item]);
                  createOptionRequest({ name: q, type: 'sector' });
                  return item;
                }}
                {...form.getInputProps('employee.sector')}
              />
            </Grid.Col>
            <Grid.Col span={2}>
              <Text color="blue" fw="bold" size={14}>
                Custos
              </Text>
            </Grid.Col>
            <Grid.Col span={1}>
              <TextInput
                label="Valor da diária"
                placeholder="digite o valor"
                type="text"
                value={form.values.employee.paymentPrice}
                onChange={(e) =>
                  form.setFieldValue(
                    'employee.paymentPrice',
                    formatBRL(e.target.value),
                  )
                }
              />
            </Grid.Col>
            <Grid.Col span={1}>
              <Switch
                mt={32}
                checked={form.values.employee.needTransport}
                color="teal"
                size="sm"
                label="Recebe vale-transporte?"
                thumbIcon={
                  form.values.employee.needTransport ? (
                    <IconCheck size="0.8rem" color="teal" stroke={3} />
                  ) : (
                    <IconX size="0.8rem" color="red" stroke={3} />
                  )
                }
                {...form.getInputProps('employee.needTransport')}
              />
            </Grid.Col>
            <Grid.Col span={1}>
              <TextInput
                disabled={!form.values.employee.needTransport}
                label="Valor da condução"
                placeholder="digite o valor"
                type="text"
                value={form.values.employee.transportPrice}
                onChange={(e) =>
                  form.setFieldValue(
                    'employee.transportPrice',
                    formatBRL(e.target.value),
                  )
                }
              />
            </Grid.Col>
            <Grid.Col span={1}>
              <TextInput
                label="Salário"
                placeholder="digite o valor"
                type="text"
                value={form.values.employee.salary}
                onChange={(e) =>
                  form.setFieldValue(
                    'employee.salary',
                    formatBRL(e.target.value),
                  )
                }
              />
            </Grid.Col>
            <Grid.Col span={2}>
              <Text color="blue" fw="bold" size={14}>
                Documentos
              </Text>
            </Grid.Col>
            <Grid.Col span={1}>
              <TextInput
                label="RG"
                placeholder="digite o documento"
                type="text"
                value={form.values.employee.documentRG}
                onChange={(e) =>
                  form.setFieldValue(
                    'employee.documentRG',
                    formatRG(e.target.value),
                  )
                }
              />
            </Grid.Col>
            <Grid.Col span={1}>
              <TextInput
                label="CPF"
                placeholder="digite o documento"
                type="text"
                value={form.values.employee.documentCPF}
                onChange={(e) =>
                  form.setFieldValue(
                    'employee.documentCPF',
                    formatCPF(e.target.value),
                  )
                }
              />
            </Grid.Col>
            <Grid.Col span={1}>
              <TextInput
                label="PIS"
                placeholder="digite o documento"
                type="text"
                {...form.getInputProps('employee.documentPIS')}
              />
            </Grid.Col>
            <Grid.Col span={2}>
              <Text color="blue" fw="bold" size={14}>
                Complementares
              </Text>
            </Grid.Col>
            <Grid.Col span={1}>
              <TextInput
                label="CEP"
                placeholder="ex: 01105-000"
                type="text"
                value={form.values.employee.addressZipCode}
                onChange={(e) =>
                  form.setFieldValue(
                    'employee.addressZipCode',
                    formatCEP(e.target.value),
                  )
                }
              />
            </Grid.Col>
            <Grid.Col span={1}>
              <TextInput
                label="Número"
                placeholder="ex: 458"
                type="text"
                {...form.getInputProps('employee.addressNumber')}
              />
            </Grid.Col>
            <Grid.Col span={2}>
              <TextInput
                label="Endereço"
                placeholder="ex: Rua João Teodoro"
                type="text"
                {...form.getInputProps('employee.addressStreet')}
              />
            </Grid.Col>
            <Grid.Col span={2}>
              <Text size={'sm'}>Documentos</Text>
              <CustomMultipleFileButton
                name="files"
                accept="application/pdf,image/png,image/jpeg,image/jpg,.eps,.ai,.cdr,.svg,.ps,.bin"
                label="Arquivo de justificativa"
                placeholder="selecione arquivo por vez"
                formRef={form}
                textInput="Clique para anexar o documento"
                icon={<IconFileInfo size={24} />}
              />
            </Grid.Col>

            <Grid.Col span={2}>
              <Text color="blue" fw="bold" size={14}>
                EPIs
              </Text>
            </Grid.Col>

            <Grid.Col span={2}>
              <Textarea
                label="Observações"
                placeholder="obs sobre o usuário"
                {...form.getInputProps('employee.note')}
              />
            </Grid.Col>
          </Grid>
          <Group mt={16} mb={16} position="right">
            <Button
              type="submit"
              loading={
                createUserLoader ||
                createEmployeeLoader ||
                uploadFilesLoader ||
                addEmployeeFilesLoading
              }
            >
              Salvar
            </Button>
          </Group>
        </form>
      </Container>
      <Modal
        title="Dados do novo usuário"
        opened={userInfoModal}
        onClose={() => setUserInfoModal(false)}
      >
        <Group>
          <Text fw="bold">Nome:</Text>
          <Text>{newUser?.username}</Text>
        </Group>
        <Group>
          <Text fw="bold">Código de Acesso:</Text>
          <Text>{newUser?.accessCode}</Text>
        </Group>
        <Group>
          <Text fw="bold">Senha:</Text>
          <Text>{newUser?.password}</Text>
        </Group>
        <Group
          onClick={() => rootNavigate(AppRoutePaths.USERS)}
          mt={16}
          position="right"
        >
          <Button type="button">Ok</Button>
        </Group>
      </Modal>
    </Page>
  );
}
